import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '@/services/loggingService';
import { useTranslation } from '@/hooks/useTranslation';
import { HeadingLarge, BodyMedium, Caption } from '@/components/ui/typography';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Translation wrapper component for error UI
const ErrorUI: React.FC<{ error?: Error; onRestart: () => void; onRefresh: () => void }> = ({ 
  error, 
  onRestart, 
  onRefresh 
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="h-svh max-h-svh overflow-y-auto flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-6 p-6">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <HeadingLarge className="mt-4">
            {t('error_something_wrong')}
          </HeadingLarge>
          <BodyMedium className="mt-2 text-muted-foreground">
            {t('error_unexpected')}
          </BodyMedium>
          {error?.message && (
            <Caption className="mt-2 font-mono bg-muted p-2 rounded block">
              {error.message}
            </Caption>
          )}
        </div>
        
        <div className="space-y-2">
          <Button onClick={onRestart} className="w-full">
            {t('error_try_again')}
          </Button>
          <Button variant="outline" onClick={onRefresh} className="w-full">
            {t('error_refresh_page')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error', error, {
      component: 'ErrorBoundary',
      metadata: { errorInfo }
    });
  }

  handleRestart = () => {
    this.setState({ hasError: false });
  };

  handleRefresh = async () => {
    try {
      const { useNavigationHelper } = await import('@/hooks/useNavigationHelper');
      const { safeReload } = useNavigationHelper();
      await safeReload('error-boundary');
    } catch (error) {
      console.warn('⚠️ Error boundary fallback - attempting navigation recovery:', error);
      try {
        // Try navigation-based recovery
        window.history.replaceState(null, '', window.location.pathname);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (navError) {
        console.warn('Navigation recovery failed; please refresh manually if needed:', navError);
      }
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorUI 
          error={this.state.error}
          onRestart={this.handleRestart}
          onRefresh={this.handleRefresh}
        />
      );
    }

    return this.props.children;
  }
}